import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavBar from '../components/common/TopNavBar';
import '../styles/Login.css';

const FullMockTest = () => {
  const navigate = useNavigate();
  const [starsInfo, setStarsInfo] = useState(null);
  const [recommended, setRecommended] = useState(null);
  const [exams, setExams] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  const token = localStorage.getItem('accessToken');
  const headers = { Authorization: `Bearer ${token}`, accept: 'application/json' };
  const API = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      try {
        const [starsRes, recRes, examsRes] = await Promise.all([
          fetch(`${API}/client/stars/`, { headers }),
          fetch(`${API}/client/exams/recommended`, { headers }),
          fetch(`${API}/client/exams/`, { headers }),
        ]);
        if (starsRes.ok) setStarsInfo(await starsRes.json());
        if (recRes.ok) setRecommended(await recRes.json());
        if (examsRes.ok) {
          const data = await examsRes.json();
          setExams(data.data || []);
          setTotalCount(data.count || 0);
        }
      } catch (err) {
        console.error('Failed to fetch:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredExams = exams.filter((e) => {
    if (activeFilter === 'available') return e.unlock_status === 'unlocked' && e.total_attempts === 0;
    if (activeFilter === 'completed') return e.total_attempts > 0;
    if (activeFilter === 'locked') return e.unlock_status === 'locked';
    return true;
  });

  const countByFilter = {
    all: exams.length,
    available: exams.filter(e => e.unlock_status === 'unlocked' && e.total_attempts === 0).length,
    completed: exams.filter(e => e.total_attempts > 0).length,
    locked: exams.filter(e => e.unlock_status === 'locked').length,
  };

  const renderShields = (pct) => {
    if (!pct && pct !== 0) return null;
    let count = 1;
    if (pct >= 90) count = 5;
    else if (pct >= 80) count = 4;
    else if (pct >= 70) count = 3;
    else if (pct >= 60) count = 2;
    return (
      <div className="flex gap-0.5 text-hupath-scout">
        {Array.from({ length: count }).map((_, i) => (
          <span key={i} className="material-symbols-outlined text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>shield</span>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen ob-sky-voyager hp-trail-pattern font-body text-hp-on-surface">
        <TopNavBar />
        <main className="max-w-6xl mx-auto px-6 pt-24">
          <div className="space-y-6">
            {[200, 120, 60, 200].map((h, i) => (
              <div key={i} className="bg-white/60 rounded-xl animate-pulse" style={{ height: h }} />
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen ob-sky-voyager hp-trail-pattern font-body text-hp-on-surface relative overflow-x-hidden">
      <TopNavBar />

      <main className="relative z-10 pt-24 pb-20 max-w-6xl mx-auto px-6">

        {/* Row 1: Star Progress */}
        {starsInfo && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-[0px_8px_24px_rgba(94,46,26,0.06)] border-b-4 border-hupath-gold flex items-center gap-6">
              <div className="bg-amber-50 w-20 h-20 rounded-full flex items-center justify-center border-2 border-amber-100">
                <span className="material-symbols-outlined text-amber-500 text-4xl" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-end mb-2">
                  <h2 className="font-headline font-black text-3xl text-hp-on-surface">{starsInfo.total_stars} Stars</h2>
                  <span className="text-sm font-bold text-hupath-scout font-headline">Đã mở: {starsInfo.exams_unlocked_count}/{totalCount} đề</span>
                </div>
                <div className="w-full bg-hp-surface-container-low h-4 rounded-full overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-hupath-gold to-amber-500 h-full rounded-full"
                    style={{ width: `${starsInfo.next_tier_threshold ? Math.min(100, (starsInfo.total_stars / starsInfo.next_tier_threshold) * 100) : 100}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="bg-hupath-scout text-white rounded-xl p-6 shadow-[0px_8px_24px_rgba(94,46,26,0.06)] flex flex-col justify-between relative overflow-hidden">
              <div className="absolute -right-4 -top-4 opacity-10">
                <span className="material-symbols-outlined text-9xl">lock_open</span>
              </div>
              <div className="relative z-10 flex justify-between items-start">
                <div>
                  <h3 className="font-headline font-bold text-hupath-gold text-lg flex items-center gap-2">
                    <span className="material-symbols-outlined text-xl">lock_open</span>
                    {starsInfo.next_tier_threshold
                      ? `Nhóm tiếp theo: ${starsInfo.next_tier_threshold} ⭐`
                      : 'Đã mở tất cả!'}
                  </h3>
                  {starsInfo.stars_to_next_tier != null && (
                    <p className="text-sm text-green-100">Cần thêm {starsInfo.stars_to_next_tier} ⭐ nữa</p>
                  )}
                </div>
                <button onClick={() => navigate('/dashboard')}
                  className="bg-hupath-gold text-hp-on-surface px-4 py-2 rounded-lg font-headline font-bold text-sm shadow-lg hover:scale-105 transition-transform">
                  📝 Đi luyện quiz →
                </button>
              </div>
              {starsInfo.stars_to_next_tier != null && (
                <p className="text-sm italic mt-4 text-green-100">
                  💡 Hoàn thành ~{Math.ceil(starsInfo.stars_to_next_tier / 7)} quiz nữa để mở nhóm đề mới!
                </p>
              )}
            </div>
          </div>
        )}

        {/* Row 2: Recommended Exam */}
        {recommended?.exam && (
          <div className="bg-white rounded-xl overflow-hidden shadow-[0px_8px_24px_rgba(94,46,26,0.06)] border-l-8 border-hupath-scout mb-10 flex flex-col md:flex-row items-center p-8 gap-8 relative">
            <div className="absolute right-0 top-0 w-32 h-32 bg-hupath-scout/5 rounded-bl-full pointer-events-none" />
            <div className="flex-1">
              <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-headline font-bold text-xs tracking-wider uppercase mb-3 inline-block">
                🎯 Đề xuất cho con
              </span>
              <h1 className="font-headline font-black text-4xl text-hp-on-surface mb-4">{recommended.exam.title}</h1>
              <div className="flex flex-wrap gap-2">
                {recommended.weak_tags_covered?.map((tag, i) => (
                  <span key={`w-${i}`} className="bg-red-50 text-red-600 px-3 py-1 rounded-lg border border-red-100 text-xs font-bold flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500" /> {tag}
                  </span>
                ))}
                {recommended.exam.tags?.slice(0, 3).map((tag) => (
                  <span key={tag.id} className="bg-green-50 text-green-600 px-3 py-1 rounded-lg border border-green-100 text-xs font-bold flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500" /> {tag.name}
                  </span>
                ))}
              </div>
            </div>
            <button onClick={() => navigate(`/full-mock-test/${recommended.exam.id}/attempt`)}
              className="shrink-0 hp-button-gradient text-white font-headline font-black text-xl px-10 py-5 rounded-xl shadow-lg hover:-translate-y-1 active:scale-95 transition-all flex items-center gap-3">
              Bắt đầu thi <span className="material-symbols-outlined">trending_flat</span>
            </button>
          </div>
        )}

        {/* Row 3: Filter tabs */}
        <div className="mb-8">
          <div className="flex items-center border-b border-hp-on-surface/10 gap-8 overflow-x-auto">
            {[
              { key: 'all', label: `Tất cả (${countByFilter.all})` },
              { key: 'available', label: `Chưa làm (${countByFilter.available})` },
              { key: 'completed', label: `Đã làm (${countByFilter.completed})` },
              { key: 'locked', label: `🔒 Chưa mở (${countByFilter.locked})` },
            ].map((tab) => (
              <button key={tab.key} onClick={() => setActiveFilter(tab.key)}
                className={`px-4 py-3 border-b-4 font-headline font-bold text-sm whitespace-nowrap transition-colors ${
                  activeFilter === tab.key
                    ? 'border-hupath-scout text-hupath-scout'
                    : 'border-transparent text-hp-on-surface/60 hover:text-hupath-scout'
                }`}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Row 4: Exam Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredExams.map((exam) => {
            const isCompleted = exam.total_attempts > 0;
            const isLocked = exam.unlock_status === 'locked';

            if (isLocked) {
              return (
                <div key={exam.id} className="bg-gray-50/80 rounded-xl p-6 shadow-sm border-2 border-dashed border-gray-200 opacity-70">
                  <div className="flex justify-between items-start mb-4">
                    <div className="bg-gray-200 text-gray-500 px-3 py-1 rounded-lg text-[10px] font-headline font-black uppercase tracking-widest">Đang khóa</div>
                    <span className="material-symbols-outlined text-gray-400">lock</span>
                  </div>
                  <h4 className="font-headline font-bold text-xl text-gray-400 mb-2">{exam.title}</h4>
                  <div className="flex items-center gap-2 mt-auto pt-6 border-t border-gray-100">
                    <span className="material-symbols-outlined text-amber-500 text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                    <span className="font-headline font-bold text-gray-500 text-sm">Cần {exam.stars_required} ⭐</span>
                  </div>
                </div>
              );
            }

            if (isCompleted) {
              const pct = exam.highest_score ? Math.round((exam.highest_score / (exam.max_score || 100)) * 100) : 0;
              return (
                <div key={exam.id} className="bg-white rounded-xl p-6 shadow-[0px_8px_24px_rgba(94,46,26,0.06)] group hover:shadow-lg transition-all border-t-4 border-hupath-scout">
                  <div className="flex justify-between items-start mb-4">
                    <div className="bg-green-50 text-green-700 px-3 py-1 rounded-lg text-[10px] font-headline font-black uppercase tracking-widest">Hoàn thành</div>
                    {renderShields(pct)}
                  </div>
                  <h4 className="font-headline font-bold text-xl text-hp-on-surface mb-2">{exam.title}</h4>
                  <div className="flex items-center gap-2 mb-6">
                    <div className="flex-1 bg-gray-100 h-2 rounded-full overflow-hidden">
                      <div className="bg-hupath-scout h-full rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="font-headline font-black text-hupath-scout">{pct}%</span>
                  </div>
                  <button onClick={() => navigate(`/full-mock-test/${exam.id}/attempt`)}
                    className="w-full py-3 rounded-lg border-2 border-hupath-scout text-hupath-scout font-headline font-bold hover:bg-hupath-scout/5 transition-colors flex items-center justify-center gap-2">
                    Thi lại <span className="material-symbols-outlined text-lg">replay</span>
                  </button>
                </div>
              );
            }

            return (
              <div key={exam.id} className="bg-white rounded-xl p-6 shadow-[0px_8px_24px_rgba(94,46,26,0.06)] group hover:shadow-lg transition-all border-t-4 border-hupath-gold">
                <div className="flex justify-between items-start mb-4">
                  <div className="bg-amber-50 text-amber-700 px-3 py-1 rounded-lg text-[10px] font-headline font-black uppercase tracking-widest">Chưa làm</div>
                  <span className="material-symbols-outlined text-amber-400">schedule</span>
                </div>
                <h4 className="font-headline font-bold text-xl text-hp-on-surface mb-2">{exam.title}</h4>
                <p className="text-xs text-hp-on-surface/50 font-body mb-6">{exam.total_duration_minutes || 45} phút</p>
                <button onClick={() => navigate(`/full-mock-test/${exam.id}/attempt`)}
                  className="w-full py-3 rounded-lg hp-button-gradient text-white font-headline font-bold shadow-lg shadow-hupath-scout/20 transition-all flex items-center justify-center gap-2">
                  Thi ngay <span className="material-symbols-outlined text-lg">play_arrow</span>
                </button>
              </div>
            );
          })}
        </div>

        {filteredExams.length === 0 && (
          <div className="text-center py-16 text-hp-on-surface/40">
            <span className="material-symbols-outlined text-6xl mb-4 block">search_off</span>
            <p className="font-headline font-bold text-lg">Không có đề thi nào trong mục này</p>
          </div>
        )}
      </main>

      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[150px] bg-white/60 blur-[60px] rounded-[100%] pointer-events-none -z-10" />
      <div className="fixed top-20 right-[-100px] w-64 h-64 bg-amber-200/20 blur-[80px] rounded-full pointer-events-none -z-10" />
      <div className="fixed bottom-20 left-[-100px] w-80 h-80 bg-hupath-scout/10 blur-[100px] rounded-full pointer-events-none -z-10" />
    </div>
  );
};

export default FullMockTest;
